/**
 * Beginner Mode: one screen on your first turn (round 1, first pass) in each game, once your big seat has
 * assembled (the seats have glided into place): how to roll, how you know you can attack, saving dice, and
 * your power. It doesn't pause the game; it's your turn anyway.
 */
import { useEffect, useState } from 'react';
import { t, type GameView } from '@kadice/shared';

/**
 * True once the guide should show. `open`: your big seat (with your controls) is up and no other screen is.
 * `delayMs`: how long the panel takes to assemble.
 */
export function useFirstTurnGuide(
  view: GameView,
  enabled: boolean,
  open: boolean,
  delayMs: number,
): { show: boolean; pending: boolean; done: () => void } {
  const [doneFor, setDoneFor] = useState<number | null>(null);
  const [readyFor, setReadyFor] = useState<number | null>(null);
  const turn = view.turn;
  // The guide is still to come (or up) on this turn, whether or not the panel is open yet.
  const pending =
    enabled &&
    view.round === 1 &&
    view.sequence === 1 &&
    !!turn &&
    turn.playerId === view.you.id &&
    !turn.hasRolled &&
    !turn.bonus &&
    !turn.extra &&
    doneFor !== view.gameId;
  const want = pending && open;
  const gameId = view.gameId;
  useEffect(() => {
    if (!want) return;
    const timer = setTimeout(() => setReadyFor(gameId), delayMs);
    return () => clearTimeout(timer);
  }, [want, gameId, delayMs]);
  // Only while it is still your first turn: a guide held back (by an animation or another screen)
  // until that turn is over is dropped, never shown on a later turn.
  return {
    show: pending && readyFor === gameId,
    pending,
    done: () => setDoneFor(gameId),
  };
}

/** `highlight`: the Highlight Attackable Enemies option, which makes killable cards glow. */
export function FirstTurnGuide({ onDone, highlight }: { onDone: () => void; highlight: boolean }) {
  return (
    <div className="modal-backdrop">
      <div className="modal splash first-turn-guide" role="dialog" aria-modal="true">
        <h2>{t('firstTurn.title')}</h2>
        <ol>
          <li>{t('firstTurn.roll')}</li>
          <li>
            {t('firstTurn.attack')} {highlight ? t('firstTurn.glow') : t('firstTurn.noGlow')} {t('firstTurn.hint')}
          </li>
          <li>{t('firstTurn.save')}</li>
        </ol>
        <p className="first-turn-power">{t('firstTurn.power')}</p>
        <div className="row actions">
          <button className="primary intro-next" onClick={onDone}>
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
